import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Playlist from '../../models/playlist/playlist';
import SyncSource from '../../models/playlist/syncSource';
import {
  hasPendingLocalChanges,
  isPlaylistSyncType,
  localSignature,
  playlistSources,
  pushesToSource,
  syncSourceKey,
} from '../../models/playlist/syncSourceHelpers';
import SyncType from '../../models/playlist/syncType';
import { GoogleApiContext, GoogleScope } from '../gapi/gapiService';
import { LibraryContext } from '../library/libraryService';
import { SoundCloudContext } from '../soundCloud/soundCloudService';
import { Context } from '../_globalContext/context';
import createSoundCloudSyncProvider from './soundCloudSyncProvider';
import createYouTubeSyncProvider from './youTubeSyncProvider';

/**
 * How long a playlist source is left alone after a sync before selecting its
 * playlist, or opening the provider's page, is allowed to sync it again.
 * Flipping between playlists should not cost a round trip every time.
 */
const youTubeSyncIntervalMs = 5 * 60 * 1000;

/**
 * How long after the last local edit to a synced playlist the push to YouTube
 * runs. Long enough to batch a burst of adds, short enough to feel immediate.
 */
const localChangeDebounceMs = 3000;

export interface ISyncContext {
  /** Playlist ids with a sync queued or running. For status icons. */
  syncingPlaylistIds: ReadonlySet<string>;
  isSyncing: boolean;
  /** Signed in to Google with the YouTube scope, so YouTube sources can sync at all. */
  canSyncYouTube: boolean;
  /** Signed in to SoundCloud, so SoundCloud sources can sync at all. */
  canSyncSoundCloud: boolean;
  /**
   * Sync the playlist's remote-playlist sources. Skips sources synced
   * recently unless forced. Call when the user opens a synced playlist.
   */
  syncPlaylist(pl: Playlist, force?: boolean): void;
  /**
   * syncPlaylist for every playlist that has a source of this type. Call
   * when the provider's page opens; omit the type for every type.
   */
  syncAllPlaylists(force?: boolean, type?: SyncType): void;
  /** syncAllPlaylists for YouTube. Call when the YouTube page opens. */
  syncAllYouTube(force?: boolean): void;
  /** syncAllPlaylists for SoundCloud. Call when the SoundCloud page opens. */
  syncAllSoundCloud(force?: boolean): void;
}
export const SyncContext = React.createContext({} as ISyncContext);

/** Once a day, for the sources that sync on a schedule (podcasts). */
function isDueDaily(source: SyncSource) {
  if (source.lastUpdated <= 0) {
    return true;
  }

  const last = new Date(source.lastUpdated);
  const now = new Date();

  return (
    last.getFullYear() < now.getFullYear() ||
    last.getMonth() < now.getMonth() ||
    last.getDate() < now.getDate()
  );
}

interface Props {
  children: React.ReactNode;
}

export default function SyncService({ children }: Props) {
  const ctx = useContext(Context);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const libCtx = useContext(LibraryContext);
  const libCtxRef = useRef(libCtx);
  libCtxRef.current = libCtx;
  const gapiCtx = useContext(GoogleApiContext);
  const gapiCtxRef = useRef(gapiCtx);
  gapiCtxRef.current = gapiCtx;

  const soundCloudCtx = useContext(SoundCloudContext);
  const soundCloudCtxRef = useRef(soundCloudCtx);
  soundCloudCtxRef.current = soundCloudCtx;

  const canSyncYouTube =
    gapiCtx.gapiClientLoaded &&
    gapiCtx.gapiAuthorized &&
    gapiCtx.authorizedScopes.indexOf(GoogleScope.YouTube) !== -1;
  const canSyncYouTubeRef = useRef(canSyncYouTube);
  canSyncYouTubeRef.current = canSyncYouTube;

  const canSyncSoundCloud = !!soundCloudCtx.accessToken;

  /**
   * Whether a source of this type can sync right now. Both providers refuse
   * politely when signed out, but a sync that cannot run must not be marked
   * as done either - see the deferral in runSync.
   */
  const canSyncType = useCallback(function canSyncType(type: SyncType) {
    switch (type) {
      case SyncType.YouTubePlaylist:
        return canSyncYouTubeRef.current;

      case SyncType.SoundCloudPlaylist:
        return !!soundCloudCtxRef.current.accessToken;

      default:
        // Podcasts and the folder types need no sign-in of their own.
        return true;
    }
  }, []);

  /**
   * Whether changes made in Fadila can reach this type's remote at all.
   * YouTube needs a scope the user grants separately; signing in to
   * SoundCloud is enough to write to your own playlists.
   */
  const canPushType = useCallback(function canPushType(type: SyncType) {
    switch (type) {
      case SyncType.YouTubePlaylist:
        return gapiCtxRef.current.canWriteYouTube;

      case SyncType.SoundCloudPlaylist:
        return !!soundCloudCtxRef.current.accessToken;

      default:
        return false;
    }
  }, []);

  /**
   * Which (playlist, source) pairs have a sync queued or running, and whether
   * another was asked for while one was in flight. Refs, because the queue
   * callbacks that maintain them must see the current sets, not a render's.
   */
  const inFlight = useRef(new Set<string>());
  const rerunRequested = useRef(new Set<string>());
  /** Per playlist count of in-flight syncs, projected into state for the UI. */
  const inFlightPerPlaylist = useRef(new Map<string, number>());
  const [syncingPlaylistIds, setSyncingPlaylistIds] = useState<ReadonlySet<string>>(new Set());

  const publishSyncing = useCallback(function publishSyncing() {
    setSyncingPlaylistIds(new Set(inFlightPerPlaylist.current.keys()));
  }, []);

  const markSyncing = useCallback(
    function markSyncing(playlistId: string, delta: 1 | -1) {
      const counts = inFlightPerPlaylist.current;
      const next = (counts.get(playlistId) || 0) + delta;

      if (next > 0) {
        counts.set(playlistId, next);
      } else {
        counts.delete(playlistId);
      }

      publishSyncing();
    },
    [publishSyncing],
  );

  /**
   * Local YouTube content, per source, as it stood when a push was last
   * attempted for it. Stops a change that fails to push from being retried
   * every three seconds; a further local edit, or a manual sync, tries again.
   */
  const attemptedSignatures = useRef(new Map<string, string>());

  /** runSync calls itself from a queue callback; a ref keeps that off its dependency list. */
  const runSyncRef = useRef<(source: SyncSource, pl: Playlist) => void>(() => {});

  const runSync = useCallback(
    function runSync(source: SyncSource, pl: Playlist) {
      const provider = ctxRef.current.syncProviders[source.type];

      if (!provider) {
        console.debug('No sync provider registered for source type', source.type);
        return;
      }

      // Not signed in to the service: leave the source exactly as it is,
      // timestamp included, so the request is still standing when a token
      // arrives - resumeSyncsOnSignIn below picks it up then. Stamping it here
      // would have the throttle hide the playlist for five minutes instead.
      if (!canSyncType(source.type)) {
        console.debug('Sync deferred, not signed in', SyncType[source.type], pl.name);
        return;
      }

      const key = syncSourceKey(pl, source);

      // One at a time per source. The provider re-reads the playlist when it
      // runs, so a request that arrives mid-flight only needs to be remembered.
      if (inFlight.current.has(key)) {
        rerunRequested.current.add(key);
        return;
      }

      inFlight.current.add(key);
      markSyncing(pl.id, 1);

      // Stamped now, not when the provider runs: stops the same sync being
      // queued again by an effect that fires before the queue gets to it.
      source.lastUpdated = Date.now();

      console.debug('synchronizing playlist', pl.name, source);

      ctxRef.current.unifiedReqQueue
        // A big playlist is pages of API calls; give it longer than a tag read
        // before the queue's watchdog decides it is never coming back.
        .queue(() => provider(source, pl), { label: 'playlist sync', timeoutMs: 15 * 60 * 1000 })
        .catch(ctxRef.current.callSnackbar)
        .finally(function syncSettled() {
          inFlight.current.delete(key);
          markSyncing(pl.id, -1);

          // Settled cleanly? Then the fingerprint is stale and the next local
          // edit must be allowed through. Otherwise keep it, to avoid a loop.
          const fresh = libCtxRef.current.playlists.find((x) => x.id === pl.id);
          const freshSource = fresh?.syncSources.find(
            (x) => x.type === source.type && x.sourceId === source.sourceId,
          );

          if (!fresh || !freshSource || !hasPendingLocalChanges(fresh, freshSource)) {
            attemptedSignatures.current.delete(key);
          }

          if (rerunRequested.current.delete(key) && fresh && freshSource) {
            runSyncRef.current(freshSource, fresh);
          }
        });
    },
    [markSyncing, canSyncType],
  );
  runSyncRef.current = runSync;

  const syncPlaylist = useCallback(
    function syncPlaylist(pl: Playlist, force = false, type?: SyncType) {
      for (const source of playlistSources(pl)) {
        if (type !== undefined && source.type !== type) {
          continue;
        }

        const due =
          force ||
          source.lastUpdated <= 0 ||
          Date.now() - source.lastUpdated > youTubeSyncIntervalMs;

        if (due) {
          runSync(source, pl);
        }
      }
    },
    [runSync],
  );

  const syncAllPlaylists = useCallback(
    function syncAllPlaylists(force = false, type?: SyncType) {
      for (const pl of libCtxRef.current.playlists) {
        syncPlaylist(pl, force, type);
      }
    },
    [syncPlaylist],
  );

  const syncAllYouTube = useCallback(
    function syncAllYouTube(force = false) {
      syncAllPlaylists(force, SyncType.YouTubePlaylist);
    },
    [syncAllPlaylists],
  );

  const syncAllSoundCloud = useCallback(
    function syncAllSoundCloud(force = false) {
      syncAllPlaylists(force, SyncType.SoundCloudPlaylist);
    },
    [syncAllPlaylists],
  );

  /**
   * The scheduled pass, run over playlists as they appear (which includes
   * every update, since updatePlaylist creates a new object).
   *
   * Playlist sources (YouTube, SoundCloud) are deliberately not on the daily
   * schedule, and so do not sync at startup. They sync when their playlist is
   * opened, when the provider's page is opened, after a local edit, and on an
   * explicit request - which is what `lastUpdated <= 0` means, both for a
   * newly linked source and for the Synchronize menu item.
   */
  const processPlaylists = useCallback(
    function processPlaylists(playlists: Playlist[], requestedOnly = false) {
      for (const pl of playlists) {
        for (const source of pl.syncSources) {
          if (isPlaylistSyncType(source.type)) {
            if (source.lastUpdated <= 0) {
              runSync(source, pl);
            }
          } else if (!requestedOnly && isDueDaily(source)) {
            runSync(source, pl);
          }
        }
      }
    },
    [runSync],
  );

  const previousPlaylists = useRef<Playlist[]>(undefined);

  useEffect(
    function watchForNewPlaylists() {
      const newPlaylists = libCtx.playlists.filter((x) => !previousPlaylists.current?.includes(x));

      processPlaylists(newPlaylists);
      previousPlaylists.current = libCtx.playlists;
    },
    [libCtx.playlists, processPlaylists],
  );

  /**
   * A link made, or a Synchronize pressed, while signed out was deferred by
   * runSync. Nothing about the playlists changes when the token arrives, so
   * watchForNewPlaylists will not notice; this does. Runs for either
   * service's sign-in and lets runSync's own check filter out the one that
   * is still signed out.
   */
  useEffect(
    function resumeSyncsOnSignIn() {
      if (canSyncYouTube || canSyncSoundCloud) {
        processPlaylists(libCtxRef.current.playlists, true);
      }
    },
    [canSyncYouTube, canSyncSoundCloud, processPlaylists],
  );

  /**
   * "You add or remove a track on the Fadila side, update the YouTube side."
   *
   * Compares each pushing source's snapshot with the playlist's YouTube
   * content and, when they differ, syncs after a short pause. Without write
   * access there is nothing to push, so nothing is scheduled - the sync on
   * opening the playlist covers the pull.
   */
  useEffect(
    function pushLocalChanges() {
      // The map object itself never changes; the cleanup wants the same one.
      const attempted = attemptedSignatures.current;
      const dirty: Array<[Playlist, SyncSource]> = [];

      for (const pl of libCtx.playlists) {
        for (const source of playlistSources(pl)) {
          if (
            !canPushType(source.type) ||
            !pushesToSource(source) ||
            !hasPendingLocalChanges(pl, source)
          ) {
            continue;
          }

          const key = syncSourceKey(pl, source);
          const signature = localSignature(pl, source);

          if (attempted.get(key) !== signature) {
            attempted.set(key, signature);
            dirty.push([pl, source]);
          }
        }
      }

      if (!dirty.length) {
        return;
      }

      let fired = false;

      const timer = setTimeout(function pushNow() {
        fired = true;

        for (const [pl, source] of dirty) {
          // The playlist may have changed again since; runSync re-reads anyway,
          // but hand it the current object so lastUpdated lands on the right one.
          const fresh = libCtxRef.current.playlists.find((x) => x.id === pl.id);
          const freshSource = fresh?.syncSources.find(
            (x) => x.type === source.type && x.sourceId === source.sourceId,
          );

          if (fresh && freshSource) {
            runSync(freshSource, fresh);
          }
        }
      }, localChangeDebounceMs);

      return function cancelPush() {
        // Once fired, the fingerprints stand: runSync clears them itself when
        // the sync settles cleanly, and keeps them when it did not - which is
        // what stops a failing push from being retried every few seconds.
        if (fired) {
          return;
        }

        clearTimeout(timer);
        // Not attempted after all; let the next pass consider them again.
        for (const [pl, source] of dirty) {
          attempted.delete(syncSourceKey(pl, source));
        }
      };
      // canWriteYouTube and the SoundCloud token are read through refs by
      // canPushType, so they are listed here to re-run the pass when either
      // arrives - a change made before signing in is still pending afterwards.
    },
    [libCtx.playlists, gapiCtx.canWriteYouTube, canSyncSoundCloud, canPushType, runSync],
  );

  const youTubeSyncProvider = useMemo(
    () =>
      createYouTubeSyncProvider({
        gapi: () => gapiCtxRef.current,
        lib: () => libCtxRef.current,
        ctx: () => ctxRef.current,
      }),
    [],
  );

  useEffect(
    function registerYouTubeSyncProvider() {
      ctx.addSyncProvider(SyncType.YouTubePlaylist, youTubeSyncProvider);
      // addSyncProvider is stable; ctx as a whole is not, and re-registering
      // on every ctx change would be pointless churn.
    },
    [youTubeSyncProvider],
  );

  const soundCloudSyncProvider = useMemo(
    () =>
      createSoundCloudSyncProvider({
        soundCloud: () => soundCloudCtxRef.current,
        lib: () => libCtxRef.current,
        ctx: () => ctxRef.current,
      }),
    [],
  );

  useEffect(
    function registerSoundCloudSyncProvider() {
      ctx.addSyncProvider(SyncType.SoundCloudPlaylist, soundCloudSyncProvider);
    },
    [soundCloudSyncProvider],
  );

  const ctxValue = useMemo<ISyncContext>(
    () => ({
      syncingPlaylistIds,
      isSyncing: syncingPlaylistIds.size > 0,
      canSyncYouTube,
      canSyncSoundCloud,
      syncPlaylist,
      syncAllPlaylists,
      syncAllYouTube,
      syncAllSoundCloud,
    }),
    [
      syncingPlaylistIds,
      canSyncYouTube,
      canSyncSoundCloud,
      syncPlaylist,
      syncAllPlaylists,
      syncAllYouTube,
      syncAllSoundCloud,
    ],
  );

  return <SyncContext.Provider value={ctxValue}>{children}</SyncContext.Provider>;
}
